/**
 * Date-format vocabulary — ONE definition of every value `dateFormat` can hold, so the
 * client and the server can never accept (or read) different sets.
 *
 * The bug this exists to prevent: the transaction-matching date column filter sent the typed
 * text with NO format at all, and the repository interpolated it into a Postgres `::date`
 * cast on a server whose `datestyle` is `ISO, MDY`. A user reading a DD/MM/YYYY cell and
 * typing "05/07/2021" back got `'05/07/2021'::date` = 2021-05-07 — HTTP 200 with a DIFFERENT
 * month's rows, and no error anywhere. "25/07/2026" was worse: month 25 → HTTP 500.
 *
 * The near-miss it also prevents: this file has TWO candidate source lists and they DISAGREE
 * (see below). Deriving a request enum from the shorter one would 400 the commonest client.
 *
 * Consumers (all derive, none restates):
 *  - frontend `EuropeanDateFormat` (shared/types/services.types.ts)
 *  - backend  `DateFilterFormat` + the /matching route enums
 *    (platform/shared-utils/localized-date-filter.utils.ts)
 */

import { DATE_FORMAT_OPTIONS, SUPPORTED_DATE_FORMATS } from './constants.js'

/**
 * Every value the client can put in `dateFormat` — the four tokens the preferences UI offers
 * and the frontend config type holds.
 *
 * SUPERSET of `SupportedDateFormat` (SUPPORTED_DATE_FORMATS), which lists only the three
 * tokens a preference may be PERSISTED as. The difference is 'DD.MM.YYYY': the frontend
 * `userConfigService` ships it as its in-memory DEFAULT and renders it, so it arrives on
 * requests from every user who never changed the setting. A server enum built from the
 * persisted subset would reject exactly those users.
 */
export type DateFormatValue = (typeof DATE_FORMAT_OPTIONS)[number]['value']

/**
 * Runtime list of the whole vocabulary — the oracle both packages' contract tests assert
 * against, and the source of the backend's request enum.
 */
export const ALL_DATE_FORMAT_VALUES: readonly DateFormatValue[] = DATE_FORMAT_OPTIONS.map(
  (option) => option.value,
)

/**
 * Compile-time proof that the persistable subset stays INSIDE the vocabulary. If
 * SUPPORTED_DATE_FORMATS ever gained a token DATE_FORMAT_OPTIONS does not offer, this
 * assignment would stop typechecking instead of silently 400ing that stored preference.
 */
const _PERSISTED_FORMATS_ARE_PART_OF_THE_VOCABULARY: readonly DateFormatValue[] =
  SUPPORTED_DATE_FORMATS
void _PERSISTED_FORMATS_ARE_PART_OF_THE_VOCABULARY

/** Runtime guard for values arriving from storage / an untyped API client. */
export function isDateFormatValue(candidate: string): candidate is DateFormatValue {
  return (ALL_DATE_FORMAT_VALUES as readonly string[]).includes(candidate)
}
